import { defineStore } from 'pinia'

import {
    createRtbParameter,
    updateRtbParameter,
    deleteRtbParameter,
} from '@/api/call-tracking/campaign/campaign-rtb-parameters-routes'

import { useDefaultStore } from '@/store/defaultStore'
import { useDetailCampaignStore } from '@/store/call-tracking/campaigns/detailCampaignStore'
import { useDetailCampaignRtbModifiersStore } from '@/store/call-tracking/campaigns/detailCampaignRtbModifiersStore'
import { useDetailCampaignRtbFiltersStore } from '@/store/call-tracking/campaigns/detailCampaignRtbFiltersStore'

import { PARAMETER_TYPES } from '@/constants/call-tracking/campaign/rtb/parameterTypes'

export const useDetailCampaignRtbParametersStore = defineStore('detailCampaignRtbParameters', {
    state: () => ({
        actionLoading: false,

        default_parameters: {
            parameters: [],
        },
        custom_parameters: {
            parameters: [],
        },
    }),

    getters: {
        campaignSlug() {
            const detailCampaignStore = useDetailCampaignStore()
            return detailCampaignStore.slug
        },
    },

    actions: {
        findParameterById(id) {
            const items = [...this.default_parameters.parameters, ...this.custom_parameters.parameters]

            const findItem = items.find((item) => item.id === id)

            if (findItem) {
                return findItem
            }

            return {}
        },

        findDuplicate(items) {
            const detailCampaignRtbParametersStore = useDetailCampaignRtbParametersStore()

            return items.some((item) => {
                const { key } = detailCampaignRtbParametersStore.findParameterById(item.parameter_id)
                return key === PARAMETER_TYPES.is_duplicate
            })
        },

        setParameters(payload) {
            Object.keys(payload).forEach((key) => {
                this[key] = payload[key]
            })
        },

        updateParametersAfterRemoveParameter(id) {
            this.custom_parameters.parameters = this.custom_parameters.parameters.filter((item) => item.id !== id)
        },

        updateParametersAfterChangeParameter(newParameter) {
            const findIndex = this.custom_parameters.parameters.findIndex((item) => item.id === newParameter.id)

            if (findIndex !== -1) {
                this.custom_parameters.parameters.splice(findIndex, 1, newParameter)
            }
        },

        async goToCreateRtbParameter(formData) {
            this.actionLoading = true

            const defaultStore = useDefaultStore()

            const { success, payload, message } = await createRtbParameter({
                slug: this.campaignSlug,
                formData,
            })

            if (success) {
                this.custom_parameters.parameters.push(payload)
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToUpdateRtbParameter(formData, parameterId) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, payload, message } = await updateRtbParameter({
                slug: this.campaignSlug,
                parameterId,
                formData,
            })

            if (success) {
                this.updateParametersAfterChangeParameter(payload)
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },

        async goToDeleteRtbParameter({ id }) {
            const defaultStore = useDefaultStore()
            const detailCampaignRtbModifiersStore = useDetailCampaignRtbModifiersStore()
            const detailCampaignRtbFiltersStore = useDetailCampaignRtbFiltersStore()

            this.actionLoading = true

            const { success, message } = await deleteRtbParameter({
                slug: this.campaignSlug,
                parameterId: id,
            })

            if (success) {
                this.updateParametersAfterRemoveParameter(id)
                detailCampaignRtbModifiersStore.updateModifiersAfterRemoveParameter(id)
                detailCampaignRtbFiltersStore.updateFiltersAfterRemoveParameter(id)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return { success }
        },
    },
})
